import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "w-[30rem]" }
const _hoisted_2 = { class: "" }
const _hoisted_3 = {
  key: 0,
  class: "font-semibold py-3 pt-4 px-10 rounded-t-box bg-base-content/10"
}
const _hoisted_4 = { class: "" }
const _hoisted_5 = { class: "" }
const _hoisted_6 = {
  key: 0,
  class: "px-4 pt-3 pb-4 pl-6"
}

import { computed, reactive, ref, watch, onUnmounted } from "vue";
import {
  KeyCode,
  ActionInternal,
  ActionInternalNested,
  isHoldTapInternal,
  isMultipleActionsInternal,
  HoldTapInternal,
  HoldTapSettings,
  isNoOp,
  isTrans,
  isKeyCode,
  isMultipleKeyCodes,
  isLayer,
  isDefaultLayer,
  isCustom,
  KEYCODES,
  NonKeycodeAction,
  flattenCustomAction,
  unflattenNonkeycodeAction,
} from "@/keyboard";
import { useActionMenuCacheStore, useKeyboardStore } from "@/stores";
import ActionSubmenu from "@/components/actions/ActionSubmenu.vue";
import HoldTapOptions from "@/components/actions/HoldTapOptions.vue";

type Internal = {
  tapKeycodes: KeyCode[];
  holdKeycodes: KeyCode[];
  tapActions: NonKeycodeAction[];
  holdActions: NonKeycodeAction[];
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ActionMenu',
  props: {
    action: {},
    cacheKey: {}
  },
  emits: ["newAction"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const kb = useKeyboardStore();
const cache = useActionMenuCacheStore();

// TODO: less hacky way to avoid loosing local options
// Because this component gets unmounted via v-if we have to preserve state between
// mounts. State is cached by row-col key.
const data = cache.get(props.cacheKey);

const useHold = ref(
  data.useHold ||
    // Force useHold if holdtap action is on.
    (isHoldTapInternal(props.action) && !isNoOp(props.action.HoldTap.hold))
);

const initialSettings = isHoldTapInternal(props.action)
  ? props.action.HoldTap.settings
  : undefined;
const useLocalHoldTapOpts = ref(initialSettings != undefined);
const holdTapOpts = reactive<HoldTapSettings>({
  config: initialSettings?.config ?? kb.defaults.config,
  tap_hold_interval:
    initialSettings?.tap_hold_interval ?? kb.defaults.tap_hold_interval,
  timeout: initialSettings?.timeout ?? kb.defaults.timeout,
});
const holdActionCache = ref(data.holdActionCache);

onUnmounted(() => {
  // Do not cache holdTapOpts because this is unintuitive - if someone changes defaults
  // then they most likely expect that they get the new defaults.
  data.useHold = useHold.value;
  data.useLocal = useLocalHoldTapOpts.value;
  data.holdActionCache = holdActionCache.value;
});

const remainingKeycodes = (used: KeyCode[]): KeyCode[] =>
  KEYCODES.filter((kc) => !used.includes(kc));

// Representation of an action in the menu, splitting 'hold' and 'tap' keys/actions as in the submenus.
const actionKeycodes = (act: ActionInternal): KeyCode[] => {
  if (
    isNoOp(act) ||
    isTrans(act) ||
    isLayer(act) ||
    isDefaultLayer(act) ||
    isCustom(act)
  )
    return [];
  else if (isKeyCode(act)) return [act.KeyCode];
  else if (isMultipleKeyCodes(act)) return act.MultipleKeyCodes;
  else if (isHoldTapInternal(act))
    throw Error("HoldTap supported only at root of action tree");
  else if (isMultipleActionsInternal(act))
    return act.MultipleActions.flatMap(actionKeycodes);
  throw Error("Unknown action type");
};

const actionNonKeycodes = (act: ActionInternal): NonKeycodeAction[] => {
  if (isNoOp(act) || isKeyCode(act) || isMultipleKeyCodes(act)) return [];
  else if (isTrans(act) || isLayer(act) || isDefaultLayer(act)) return [act];
  else if (isCustom(act)) return [flattenCustomAction(act)];
  else if (isHoldTapInternal(act))
    throw Error("HoldTap supported only at root of action tree");
  else if (isMultipleActionsInternal(act))
    return act.MultipleActions.flatMap(actionNonKeycodes);
  throw Error("Unknown action type");
};

const holdTapToInternal = (act: HoldTapInternal): Internal => {
  const [hold, tap] = [act.HoldTap.hold, act.HoldTap.tap];
  return {
    tapKeycodes: actionKeycodes(tap),
    holdKeycodes: actionKeycodes(hold),
    tapActions: actionNonKeycodes(tap),
    holdActions: actionNonKeycodes(hold),
  };
};

const actionToInternal = (act: ActionInternal): Internal =>
  isHoldTapInternal(act)
    ? holdTapToInternal(act)
    : {
        tapKeycodes: actionKeycodes(act),
        tapActions: actionNonKeycodes(act),
        holdKeycodes: [],
        holdActions: [],
      };

const internalToAction = (internal: Internal): ActionInternal => {
  const { tapKeycodes, tapActions, holdKeycodes, holdActions } = internal;

  if (useHold.value && holdKeycodes.length + holdActions.length > 0) {
    // Recurse, making sure that hold keycodes/actions are empty to avoid infinite recursion
    const tap = internalToAction({
      tapKeycodes,
      tapActions,
      holdKeycodes: [],
      holdActions: [],
    });
    const hold = internalToAction({
      tapKeycodes: holdKeycodes,
      tapActions: holdActions,
      holdKeycodes: [],
      holdActions: [],
    });
    if (isHoldTapInternal(tap) || isHoldTapInternal(hold))
      throw Error("Assertion failed: nested resolved to HoldTap");
    if (isNoOp(hold)) throw Error("Assertion failed: Hold resolved to NoOp");

    const settings = useLocalHoldTapOpts.value
      ? {
          config: holdTapOpts.config,
          timeout: holdTapOpts.timeout,
          tap_hold_interval: holdTapOpts.tap_hold_interval,
        }
      : undefined;
    return {
      HoldTap: {
        hold,
        tap,
        settings,
      },
    };
  }

  // Resolve non hold-tap action
  tapActions.forEach((a) => {
    // ignore unflattening custom actions as we only search for HoldTap
    if (isHoldTapInternal(a as ActionInternal))
      throw Error("HoldTap supported only at root of action tree");
  });

  const actions: ActionInternalNested[] = tapActions.map(
    unflattenNonkeycodeAction
  );
  if (tapKeycodes.length == 1) actions.push({ KeyCode: tapKeycodes[0] });
  else if (tapKeycodes.length > 1)
    actions.push({ MultipleKeyCodes: tapKeycodes });

  if (actions.length == 0) return "NoOp";
  else if (actions.length == 1) return actions[0];
  else return { MultipleActions: actions };
};

const state = computed(() => actionToInternal(props.action));

const tapKeycodeOptions = computed(() =>
  remainingKeycodes(state.value.tapKeycodes)
);
const holdKeycodeOptions = computed(() =>
  remainingKeycodes(state.value.holdKeycodes)
);

const updateAction = (update: Partial<Internal>) => {
  const newState = Object.assign({}, state.value, update);
  const act = internalToAction(newState);
  console.log(JSON.stringify(act, null, 2));
  emit("newAction", act);
};

watch(
  [useHold, holdTapOpts, useLocalHoldTapOpts],
  ([newUseHold], [oldUseHold]) => {
    // Manage cached hold action
    if (newUseHold != oldUseHold) {
      if (newUseHold) {
        // Load from cache
        if (holdActionCache.value != null) {
          const { tapKeycodes, tapActions } = actionToInternal(
            holdActionCache.value
          );
          state.value.holdKeycodes = tapKeycodes;
          state.value.holdActions = tapActions;
        }
      } else if (isHoldTapInternal(props.action)) {
        // Store in cache
        holdActionCache.value = JSON.parse(
          JSON.stringify(props.action.HoldTap.hold)
        );
      }
    }
    // Just trigger the call, which will re-evaluate internalToAction
    updateAction({});
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (useHold.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, " On tap "))
        : _createCommentVNode("", true),
      _createVNode(ActionSubmenu, {
        class: "px-4 pt-3 pb-4",
        keycodes: state.value.tapKeycodes,
        keycodeOptions: tapKeycodeOptions.value,
        actions: state.value.tapActions,
        onNewKeycodes: _cache[0] || (_cache[0] = (kc) => updateAction({ tapKeycodes: kc })),
        onNewActions: _cache[1] || (_cache[1] = (a) => updateAction({ tapActions: a }))
      }, null, 8, ["keycodes", "keycodeOptions", "actions"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", {
        class: _normalizeClass(["py-3 px-10 bg-base-content/10 flex justify-between items-center", useHold.value ? 'font-semibold' : ''])
      }, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(useHold.value ? "On hold" : "Enable Hold/Tap"), 1),
        _withDirectives(_createElementVNode("input", {
          type: "checkbox",
          class: "toggle",
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((useHold).value = $event))
        }, null, 512), [
          [_vModelCheckbox, useHold.value]
        ])
      ], 2),
      (useHold.value)
        ? (_openBlock(), _createBlock(ActionSubmenu, {
            key: 0,
            class: "px-4 pt-3 pb-4",
            keycodes: state.value.holdKeycodes,
            keycodeOptions: holdKeycodeOptions.value,
            actions: state.value.holdActions,
            onNewKeycodes: _cache[3] || (_cache[3] = (kc) => updateAction({ holdKeycodes: kc })),
            onNewActions: _cache[4] || (_cache[4] = (a) => updateAction({ holdActions: a }))
          }, null, 8, ["keycodes", "keycodeOptions", "actions"]))
        : _createCommentVNode("", true)
    ]),
    (useHold.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["", useLocalHoldTapOpts.value ? '' : ''])
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["py-3 px-10 bg-base-content/10 flex justify-between items-center", useLocalHoldTapOpts.value ? 'font-semibold' : 'rounded-b-box'])
          }, [
            _cache[9] || (_cache[9] = _createElementVNode("span", { class: "" }, "Hold/Tap overrides", -1)),
            _withDirectives(_createElementVNode("input", {
              type: "checkbox",
              class: "toggle",
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((useLocalHoldTapOpts).value = $event))
            }, null, 512), [
              [_vModelCheckbox, useLocalHoldTapOpts.value]
            ])
          ], 2),
          (useLocalHoldTapOpts.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(HoldTapOptions, {
                  timeout: holdTapOpts.timeout,
                  "onUpdate:timeout": _cache[6] || (_cache[6] = ($event: any) => ((holdTapOpts.timeout) = $event)),
                  tap_hold_interval: holdTapOpts.tap_hold_interval,
                  "onUpdate:tap_hold_interval": _cache[7] || (_cache[7] = ($event: any) => ((holdTapOpts.tap_hold_interval) = $event)),
                  config: holdTapOpts.config,
                  "onUpdate:config": _cache[8] || (_cache[8] = ($event: any) => ((holdTapOpts.config) = $event))
                }, null, 8, ["timeout", "tap_hold_interval", "config"])
              ]))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true)
  ]))
}
}

})